import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LayoutDashboard, Database, Radar, FileCheck2,
  Cpu, Users, LogOut, PlayCircle, CalendarClock, CreditCard, HardDrive, FolderLock,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Tour } from "@/components/Tour";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Overview", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/workloads", icon: Database, label: "Backup & Recovery", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/agents", icon: HardDrive, label: "Backup Agents", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/vault", icon: FolderLock, label: "My Vault", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/policies", icon: CalendarClock, label: "Backup Policies", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/threats", icon: Radar, label: "Threat Defense", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/compliance", icon: FileCheck2, label: "Compliance Vault", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/ai", icon: Cpu, label: "Vaultvec AI", roles: ["admin", "compliance", "auditor"] },
  { to: "/app/billing", icon: CreditCard, label: "Billing", roles: ["admin"] },
  { to: "/app/leads", icon: Users, label: "Contact Requests", roles: ["admin"] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const items = NAV.filter((n) => n.roles.includes(user.role));

  const tourSteps = [
    { title: "Welcome to Vaultvec", body: "A quick guided tour of the platform you'll show clients. Tap Next to walk through each module — the app tells the story for you." },
    { route: "/app", target: '[data-testid="nav-overview"]', title: "Overview", body: "Your live protection posture — protected systems, active threats, compliance score and 7-day activity, all at a glance." },
    { route: "/app/workloads", target: '[data-testid="nav-backup---recovery"]', title: "Backup & Recovery", body: "Every critical system gets verified, tamper-proof restore points. Backups run automatically on a policy you set per workload." },
    { route: "/app/threats", target: '[data-testid="nav-threat-defense"]', title: "Threat Defense", body: "Real-time ransomware and anomaly detection. Resolve threats in one click — and every action is logged for audit." },
    { route: "/app/compliance", target: '[data-testid="nav-compliance-vault"]', title: "Compliance Vault", body: "The pharma differentiator: a tamper-evident, hash-chained 21 CFR Part 11 audit trail. Hit 'Verify Chain Integrity' to prove nothing was altered." },
    { route: "/app/ai", target: '[data-testid="nav-vaultvec-ai"]', title: "Vaultvec AI", body: "AI analyzes threats and drafts an inspection-ready 21 CFR Part 11 report in seconds — then export it as a signed PDF." },
    ...(user.role === "admin"
      ? [{ route: "/app/leads", target: '[data-testid="nav-contact-requests"]', title: "Contact Requests", body: "Every message from the contact form on your marketing site lands here so your team can follow up." }]
      : []),
    { title: "You're ready to demo!", body: "That's the full story: protect, detect, prove. Sign in as different roles to show role-based access. Good luck out there!" },
  ];

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 shrink-0 border-r border-border flex flex-col fixed inset-y-0 left-0 bg-[#050505] z-30">
        <div className="ambient-spot top-0 left-0 w-full h-24 opacity-40" />
        <div className="relative h-16 flex items-center gap-2.5 px-5 border-b border-border">
          <div className="h-8 w-8 bg-primary flex items-center justify-center glow-primary"><ShieldCheck className="h-4 w-4 text-white" strokeWidth={1.5} /></div>
          <span className="font-heading text-lg font-extrabold tracking-tighter">VAULTVEC</span>
        </div>
        <nav className="relative flex-1 py-4 px-2 space-y-0.5">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-all duration-200 ${
                  isActive
                    ? "bg-primary/10 text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full transition-all ${isActive ? "bg-primary opacity-100" : "opacity-0"}`} />
                  <n.icon className={`h-4 w-4 shrink-0 transition-colors ${isActive ? "text-primary" : "group-hover:text-foreground"}`} strokeWidth={1.5} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="relative border-t border-border p-4">
          <button data-testid="start-demo-btn" onClick={() => setTourOpen(true)} className="flex items-center justify-center gap-2 w-full rounded-sm border border-primary/40 text-primary hover:bg-primary hover:text-white hover:glow-primary transition-all text-xs py-2.5 mb-4">
            <PlayCircle className="h-4 w-4" strokeWidth={1.5} /> Start Guided Demo
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-secondary flex items-center justify-center font-mono text-sm text-primary border border-border">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm truncate">{user.name}</p>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">{ROLE_LABELS[user.role]}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} className="flex items-center gap-2 w-full text-xs text-muted-foreground hover:text-primary transition-colors px-1 py-1.5">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="relative flex-1 ml-64 min-h-screen">
        <div className="noise-overlay" />
        <Outlet />
      </main>

      <Tour steps={tourSteps} open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}
